import React, { FC, PropsWithChildren, useState } from 'react';
import styled, { css } from 'styled-components';
import { Sticky } from 'react-sticky';
import { PaddingBottomProps, maxWidth } from 'styled-system';
import { Box, Flex, Grid, Paragraph } from '../UIKit/system';
import Question from '../accordion/question';
import { StyledAnswer } from '../accordion/answer';
import mq from '../../utils/mq';
import { getThemeColor } from '../../utils/styles';

export const HeadingSection = styled(Grid).attrs(() => ({
  paddingTop: [30, 52],
  paddingBottom: [30, 52]
}))``;

export const HeadingDescription = styled(Paragraph).attrs(() => ({
  as: 'p',
  marginTop: [16, 24],
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'center',
  maxWidth: [undefined, undefined, 'calc(calc(100% - 80px) / 3 * 2 + 40px)']
}))``;

export const CompareGrid = styled(Grid).attrs<PaddingBottomProps>((props) => ({
  paddingBottom: props.paddingBottom || [30, 30, 45]
}))``;

export const StickyGrid: FC<PropsWithChildren> = ({ children }) => (
  <Sticky topOffset={400}>
    {({ style, isSticky }: any) => {
      return (
        <CompareGrid
          paddingBottom={20}
          style={style}
          zIndex={1}
          backgroundColor="white"
          paddingTop={isSticky ? [60, 85, 80] : undefined}
          className={isSticky && 'sticky'}
        >
          {children}
        </CompareGrid>
      );
    }}
  </Sticky>
);

interface CompareRowProps {
  largeRowGap?: boolean;
  twoColumns?: boolean;
}

export const CompareRow = styled(Grid).attrs<CompareRowProps>((props) => ({
  gridTemplateColumns: [
    'repeat(2, 1fr)',
    'repeat(2, 1fr)',
    props.twoColumns ? 'repeat(2, calc(100% / 3))' : 'repeat(3, 1fr)'
  ],
  justifyContent: [null, null, props.twoColumns ? 'center' : null],
  gridColumnGap: [20, 20, 40],
  gridRowGap: props.largeRowGap ? [16, 24, 32] : 20,
  px: [null, null, props.twoColumns ? 40 : null]
}))<CompareRowProps>`
  ${(props) =>
    props.twoColumns &&
    css`
      & > *:last-child {
        display: none;
      }
    `}

  ${mq.tablet(css`
    & > *:last-child {
      display: none;
    }
  `)}
`;

export const GroupsGrid = styled(Grid).attrs(() => ({
  paddingBottom: [30, 60]
}))``;

export const Description = styled(Paragraph).attrs(() => ({
  fontSize: [14, 16],
  paddingTop: [16, 24],
  paddingBottom: 24
}))``;

export const QuoteBtnContainer = styled.div`
  margin-top: 8px;
`;

export const ItemBlock = styled(Grid).attrs<{
  gridColumn?: number;
  twoColumns?: boolean;
}>((props) => ({
  gridRowGap: 17,
  display:
    props.gridColumn === 3
      ? ['none', 'none', !props.twoColumns && 'grid']
      : undefined
}))``;

export const ColoursContainer = styled(Flex)`
  gap: 12px;
`;

export const ColourDot = styled(Box).attrs(() => ({
  borderRadius: '100%',
  height: [40, 58],
  width: [40, 58]
}))``;

interface ItemDescriptionProps {
  center?: boolean;
}

export const ItemDescription = styled(Paragraph).attrs<ItemDescriptionProps>(
  (props) => ({
    fontSize: 16,
    textAlign: props.center ? 'center' : undefined
  })
)`
  b,
  strong {
    font-weight: 700;
  }

  a {
    font-weight: inherit;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

export const StickyContent = styled.div`
  display: none;

  .sticky & {
    display: block;
    padding-top: 20px;
  }
`;

const StyledItem = styled.div<{ narrow?: boolean }>`
  &:after {
    content: '';
    display: block;
    background-color: ${getThemeColor('lightGrey')};
    height: 1px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
  }

  ${(props) =>
    props.narrow &&
    css`
      &:after {
        max-width: calc(calc(100% - 80px) / 3 * 2 + 40px);

        ${mq.tablet(css`
          max-width: none;
        `)}
      }
    `}

  &:last-of-type:after {
    display: none;
  }
`;

export const Accordion: FC<
  PropsWithChildren<{
    title: string;
    narrow: boolean;
  }>
> = (props) => {
  const { title, children, narrow } = props;
  const [collapsed, setCollapsed] = useState(false);

  return (
    <StyledItem narrow={narrow}>
      <QuestionWrap narrow={narrow}>
        <Question
          collapsed={collapsed}
          onClick={() => setCollapsed((state) => !state)}
          as="h3"
          fontSize={[16, 24]}
        >
          {title}
        </Question>
      </QuestionWrap>
      <StyledAnswer
        initial="hidden"
        animate={collapsed ? 'visible' : 'hidden'}
        variants={{
          hidden: { opacity: 0, height: 0 },
          visible: { opacity: 1, height: 'auto' }
        }}
        transition={{ duration: 0.35, type: 'tween' }}
        fullWidth
      >
        {children}
      </StyledAnswer>
    </StyledItem>
  );
};

interface QuestionWrapProps {
  narrow?: boolean;
}

const QuestionWrap = styled.div.attrs<QuestionWrapProps>((props) => ({
  maxWidth: [
    undefined,
    undefined,
    props.narrow && 'calc(calc(100% - 80px) / 3 * 2 + 40px)'
  ]
}))`
  ${maxWidth}
  margin-left: auto;
  margin-right: auto;
`;

export const AccordionFirst: FC<
  PropsWithChildren<{
    title: string;
    narrow: boolean;
  }>
> = (props) => {
  const { title, children, narrow } = props;
  return (
    <StyledItem narrow={narrow}>
      <QuestionWrap narrow={narrow}>
        <OpenQuestion collapsed={true} asElement="h3" fontSize={[16, 24]}>
          {title}
        </OpenQuestion>
      </QuestionWrap>
      <OpenAnswer fullWidth>{children}</OpenAnswer>
    </StyledItem>
  );
};

const OpenQuestion = styled(Question)`
  ${maxWidth}
  cursor: default;

  & > :last-child {
    display: none;
  }
`;

const OpenAnswer = styled(StyledAnswer)`
  height: auto;
`;

export const ToggleColumnsWrap = styled(Grid).attrs(() => ({
  display: ['none', 'none', 'grid'],
  alignItems: 'center',
  gridAutoFlow: 'column',
  gridColumnGap: 20,
  justifyContent: 'center',
  paddingTop: 24
}))``;
